import React, { useState, useEffect, useRef } from 'react';
import { Editor as ReactDraftEditor } from 'react-draft-wysiwyg';
import { EditorState } from 'draft-js';
import { stateToMarkdown } from 'draft-js-export-markdown';
import { stateFromMarkdown } from 'draft-js-import-markdown';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useTranslation } from 'react-i18next';

const RichTextField = ({
  initialValue,
  onChange,
  placeholder,
  expandTextField,
  maxLength,
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [errorMessage, setErrorMessage] = useState('');
  const { t: translator } = useTranslation();

  useEffect(() => {
    const newState = EditorState.moveSelectionToEnd(
      EditorState.createWithContent(stateFromMarkdown(initialValue || '')),
    );
    setEditorState(newState);
  }, [initialValue]);

  const onEditorStateChange = (newState) => {
    setEditorState(newState);
    const contentState = newState.getCurrentContent();
    if (contentState.hasText()) {
      const markdown = stateToMarkdown(contentState);
      onChange(markdown);
      setErrorMessage('');
    } else {
      onChange('');
    }
  };

  const getLengthOfSelectedText = () => {
    const currentSelection = editorState.getSelection();
    const isCollapsed = currentSelection.isCollapsed();

    let length = 0;

    if (!isCollapsed) {
      const currentContent = editorState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const startBlock = currentContent.getBlockForKey(startKey);
      const isStartAndEndBlockAreTheSame = startKey === endKey;
      const startBlockTextLength = startBlock.getLength();
      const startSelectedTextLength =
        startBlockTextLength - currentSelection.getStartOffset();
      const endSelectedTextLength = currentSelection.getEndOffset();
      const keyAfterEnd = currentContent.getKeyAfter(endKey);
      if (isStartAndEndBlockAreTheSame) {
        length +=
          currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        let currentKey = startKey;

        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }

          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }

    return length;
  };

  const handleBeforeInput = () => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    const selectedTextLength = getLengthOfSelectedText();

    if (currentContentLength - selectedTextLength > maxLength - 1) {
      setErrorMessage(translator('ariaLabel.richTextError'));
      return 'handled';
    }
  };

  const handlePastedText = (pastedText) => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    const selectedTextLength = getLengthOfSelectedText();

    if (
      currentContentLength + pastedText.length - selectedTextLength >
      maxLength
    ) {
      setErrorMessage(translator('ariaLabel.richTextError'));
      return 'handled';
    }
  };

  const editorRef = useRef();

  return (
    <div
      className={
        expandTextField
          ? 'rich-text-editor-expanded'
          : 'rich-text-editor-standard'
      }
      onClick={() => {
        editorRef.current.focusEditor();
      }}
    >
      <ReactDraftEditor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        stripPastedStyles={true}
        toolbar={toolbar}
        ref={editorRef}
        placeholder={placeholder}
        wrapperClassName="wrapper-class"
        editorClassName="editor-class"
        toolbarClassName="toolbar-class"
        ariaLabel={translator('ariaLabel.richText')}
        handleBeforeInput={maxLength && handleBeforeInput}
        handlePastedText={maxLength && handlePastedText}
      />
      {errorMessage.length > 0 && maxLength && (
        <p style={{ color: 'red' }}>{errorMessage}</p>
      )}
    </div>
  );
};

export { RichTextField };

const toolbar = {
  options: ['inline', 'blockType', 'list', 'link'],
  inline: {
    options: ['bold', 'italic'],
  },
  blockType: {
    options: ['normal', 'H2', 'H3'],
  },
  list: {
    options: ['unordered', 'ordered'],
  },
  link: {
    defaultTargetOptions: '_blank',
  },
};
